import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { supabase } from "@/lib/supabase/client";
import { useToast } from "@/hooks/use-toast";

export default function Login() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!email || !password) {
      toast({
        title: "Erro",
        description: "Preencha todos os campos",
        variant: "destructive",
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Erro",
        description: "Email inválido",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Handle specific error cases
        let errorMessage = "Credenciais inválidas";
        let showHelp = false;
        
        if (error.message.includes("Invalid login credentials") || error.status === 400) {
          errorMessage = "Email ou senha incorretos. Verifique suas credenciais.";
          showHelp = true;
        } else if (error.message.includes("Email not confirmed") || error.message.includes("email_not_confirmed")) {
          errorMessage = "Email não confirmado. Verifique sua caixa de entrada e clique no link de confirmação.";
        } else if (error.message.includes("User not found")) {
          errorMessage = "Usuário não encontrado. Verifique o email ou cadastre-se.";
        } else if (error.message.includes("Too many requests") || error.message.includes("rate_limit")) {
          errorMessage = "Muitas tentativas. Aguarde alguns minutos e tente novamente.";
        } else {
          errorMessage = error.message || "Erro ao fazer login";
        }
        
        // Log detailed error for debugging
        console.error("Login error details:", {
          message: error.message,
          status: error.status,
          name: error.name,
        });
        
        const loginError = new Error(errorMessage) as any;
        loginError.showHelp = showHelp;
        throw loginError;
      }

      if (!data.user) {
        throw new Error("Falha ao fazer login. Tente novamente.");
      }

      // Wait a bit for the AuthContext to update
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Get user profile to determine role
      const { data: profile, error: profileError } = await supabase
        .from("perfis")
        .select("role, status")
        .eq("auth_user_id", data.user.id)
        .single();

      // Check if profile exists
      if (profileError || !profile) {
        console.error("Profile error:", profileError);
        // User exists but no profile - this shouldn't happen, but handle gracefully
        toast({
          title: "Aviso",
          description: "Perfil não encontrado. Entre em contato com o suporte.",
          variant: "destructive",
        });
        // Sign out to prevent partial session
        await supabase.auth.signOut();
        return;
      }

      // Check if user is active
      if (profile.status !== "ativo") {
        toast({
          title: "Conta inativa",
          description: "Sua conta está inativa. Entre em contato com o administrador.",
          variant: "destructive",
        });
        await supabase.auth.signOut();
        return;
      }

      // Update last access
      await supabase
        .from("perfis")
        .update({ ultimo_acesso: new Date().toISOString() })
        .eq("auth_user_id", data.user.id);

      toast({
        title: "Login realizado!",
        description: "Redirecionando...",
      });

      // Redirect based on role
      const role = profile?.role || "user";
      if (role === "master") {
        navigate("/dashboard/master");
      } else if (role === "admin") {
        navigate("/dashboard/admin");
      } else {
        navigate("/dashboard/colaborador");
      }
    } catch (error: any) {
      console.error("Login error:", error);
      
      let description = error.message || "Credenciais inválidas";
      
      // Add helpful suggestions if it's a credentials error
      if (error.showHelp) {
        description += "\n\nDicas:\n• Verifique se o email está correto\n• Verifique se a senha está correta\n• Se acabou de se cadastrar, aguarde alguns segundos e tente novamente\n• Se o problema persistir, use 'Esqueceu sua senha?' para redefinir";
      }
      
      toast({
        title: "Erro ao fazer login",
        description,
        variant: "destructive",
        duration: error.showHelp ? 8000 : 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/">
            <h1 className="text-3xl font-bold bg-hero-gradient bg-clip-text text-transparent">
              Orbita Control IA
            </h1>
          </Link>
        </div>
        
        <Card className="border-border">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl">Login</CardTitle>
            <CardDescription>
              Entre com suas credenciais para acessar o sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-input border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-input border-border"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow-primary"
                disabled={loading}
              >
                {loading ? "Entrando..." : "Entrar"}
              </Button>
            </form>
            
            <div className="mt-4 space-y-2 text-center text-sm">
              <div>
                <span className="text-muted-foreground">Não tem uma conta? </span>
                <Link to="/auth/register" className="text-primary hover:underline">
                  Cadastre-se
                </Link>
              </div>
              <div>
                <Link 
                  to="/auth/forgot-password" 
                  className="text-primary hover:underline text-xs"
                >
                  Esqueceu sua senha?
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
